import { getMoscowTime } from "@infrastructure/clients/http/utils/time.utils";

/**
 * Общий логгер для API запросов с временем
 */

export interface ApiRequestLogOptions {
  method: string;
  url: string;
  baseURL?: string;
  fullURL?: string;
  headers?: Record<string, any>;
  params?: any;
  data?: any;
}

export interface ApiResponseLogOptions {
  method: string;
  url: string;
  status: number;
  statusText?: string;
  fullURL?: string;
  data?: any;
}

/**
 * Форматирует данные для логирования в читаемый вид
 */
function formatDataForLog(data: any, maxLength: number = 1000): string {
  if (!data) return "нет данных";
  
  if (data instanceof FormData) {
    return "[FormData]";
  }
  
  if (typeof data === "string") {
    return data.length > maxLength ? `${data.substring(0, maxLength)}...` : data;
  }
  
  try {
    const json = JSON.stringify(data, null, 2);
    return json.length > maxLength ? `${json.substring(0, maxLength)}...` : json;
  } catch {
    return String(data).substring(0, maxLength);
  }
}

/**
 * Логирует API запрос с временем в читаемом формате
 */
export function logApiRequest(options: ApiRequestLogOptions): void {
  const { method, url, baseURL, fullURL, headers, params, data } = options;
  const time = getMoscowTime();
  const methodUpper = method.toUpperCase();
  const urlPath = url || "unknown";
  
  // Формируем полный URL
  const finalFullURL = fullURL || (baseURL && url ? `${baseURL}${url}` : url);
  
  // Формируем читаемые строки для логов
  const parts: string[] = [];
  parts.push(`[${time}] [API Request] ${methodUpper} ${urlPath}`);
  parts.push(`  URL: ${finalFullURL}`);
  
  // Параметры запроса
  if (params && Object.keys(params).length > 0) {
    parts.push(`  Params: ${formatDataForLog(params, 500)}`);
  }
  
  // Данные запроса
  if (data) {
    if (data instanceof FormData) {
      parts.push(`  Data: [FormData]`);
    } else {
      parts.push(`  Data: ${formatDataForLog(data, 1000)}`);
    }
  }
  
  // Важные заголовки (без токенов)
  if (headers) {
    const importantHeaders: string[] = [];
    Object.keys(headers).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (lowerKey === "authorization" || lowerKey === "telegram-token") {
        importantHeaders.push(`${key}: Bearer ***`);
      } else if (lowerKey === "content-type" || lowerKey === "accept") {
        importantHeaders.push(`${key}: ${headers[key]}`);
      }
    });
    if (importantHeaders.length > 0) {
      parts.push(`  Headers: ${importantHeaders.join(", ")}`);
    }
  }
  
  console.log(parts.join("\n"));
}

/**
 * Логирует успешный API ответ с временем в читаемом формате
 */
export function logApiResponse(options: ApiResponseLogOptions): void {
  const { method, url, status, statusText, fullURL, data } = options;
  const time = getMoscowTime();
  const methodUpper = method.toUpperCase();
  const urlPath = url || "unknown";
  
  const parts: string[] = [];
  parts.push(`[${time}] [API Response] ${status} ${statusText || "OK"} ${methodUpper} ${urlPath}`);
  parts.push(`  URL: ${fullURL || urlPath}`);
  
  // Данные ответа
  if (data) {
    if (typeof data === "object" && data !== null) {
      // Если это объект с полями status/data (стандартный формат API)
      if ("status" in data && "data" in data) {
        parts.push(`  Response Status: ${data.status}`);
        if (data.data) {
          if (Array.isArray(data.data)) {
            parts.push(`  Response Data: массив из ${data.data.length} элементов`);
            if (data.data.length > 0) {
              parts.push(`  First Item: ${formatDataForLog(data.data[0], 300)}`);
            }
          } else if (typeof data.data === "object") {
            parts.push(`  Response Data: ${formatDataForLog(data.data, 1000)}`);
          } else {
            parts.push(`  Response Data: ${String(data.data)}`);
          }
        }
        if (data.details) {
          parts.push(`  Details: ${data.details}`);
        }
      } else {
        // Обычный объект
        parts.push(`  Response Data: ${formatDataForLog(data, 1000)}`);
      }
    } else {
      parts.push(`  Response Data: ${String(data)}`);
    }
  }
  
  console.log(parts.join("\n"));
}

/**
 * Логирует ошибку API ответа с временем в читаемом формате
 */
export function logApiResponseError(options: ApiResponseLogOptions & { 
  errorData?: any;
  requestData?: any;
}): void {
  const { method, url, status, statusText, fullURL, errorData, requestData } = options;
  const time = getMoscowTime();
  const methodUpper = method.toUpperCase();
  const urlPath = url || "unknown";
  
  const parts: string[] = [];
  parts.push(`[${time}] [API Response Error] ${status} ${statusText || "Error"} ${methodUpper} ${urlPath}`);
  parts.push(`  URL: ${fullURL || urlPath}`);
  
  // Что отправляли
  if (requestData) {
    parts.push(`  Request Data: ${formatDataForLog(requestData, 500)}`);
  }
  
  // Что получили (ошибка)
  if (errorData) {
    if (typeof errorData === "object" && errorData !== null) {
      // Стандартный формат ошибки API
      if ("status" in errorData && errorData.status === "error") {
        parts.push(`  Error Status: ${errorData.status}`);
        if (errorData.error_code) {
          parts.push(`  Error Code: ${errorData.error_code}`);
        }
        if (errorData.details) {
          parts.push(`  Error Details: ${errorData.details}`);
        }
        if (errorData.data) {
          parts.push(`  Error Data: ${formatDataForLog(errorData.data, 500)}`);
        }
      } else {
        parts.push(`  Error Data: ${formatDataForLog(errorData, 1000)}`);
      }
    } else {
      parts.push(`  Error Data: ${String(errorData)}`);
    }
  }
  
  console.error(parts.join("\n"));
}

